import { useEffect, useRef, useState } from 'react'
import Logo from '../../../icons/Logo'

// ── Config ────────────────────────────────────────────────────
const PARTICLE_COUNT = 220
const REVEAL_DELAY_MS = 350   // пауза после появления картинки
const REVEAL_DURATION_MS = 850 // длительность проявки

interface Particle {
	x: number
	y: number
	vx: number
	vy: number
	r: number
	baseOpacity: number
	phase: number
}

interface Props {
	src: string
	className?: string
	alt?: string
}

/**
 * Обёртка над <img> с эффектом Telegram-spoiler:
 * частицы + блюр, которые плавно исчезают, проявляя изображение.
 * При ошибке загрузки (broken link) показывает лого сайта.
 */
export function SpoilerReveal({ src, className, alt }: Props) {
	const wrapRef = useRef<HTMLDivElement>(null)
	const canvasRef = useRef<HTMLCanvasElement>(null)
	const rafRef = useRef<number>(0)
	const particlesRef = useRef<Particle[]>([])

	const [revealing, setRevealing] = useState(false)
	const [done, setDone] = useState(false)
	const [broken, setBroken] = useState(false)

	// ── Запуск анимации частиц ─────────────────────────────────
	useEffect(() => {
		const canvas = canvasRef.current
		const wrap = wrapRef.current
		if (!canvas || !wrap) return

		const w = wrap.clientWidth || 300
		const h = wrap.clientHeight || 300
		canvas.width = w
		canvas.height = h

		particlesRef.current = Array.from({ length: PARTICLE_COUNT }, () => ({
			x: Math.random() * w,
			y: Math.random() * h,
			vx: (Math.random() - 0.5) * 0.55,
			vy: (Math.random() - 0.5) * 0.55,
			r: Math.random() * 1.6 + 0.3,
			baseOpacity: Math.random() * 0.5 + 0.25,
			phase: Math.random() * Math.PI * 2,
		}))

		const loop = (now: number) => {
			const ctx = canvas.getContext('2d')
			if (!ctx) return
			const t = now / 1000

			ctx.clearRect(0, 0, w, h)

			for (const p of particlesRef.current) {
				p.x = (p.x + p.vx + w) % w
				p.y = (p.y + p.vy + h) % h
				const flicker = 0.5 + 0.5 * Math.sin(t * 2.8 + p.phase)
				ctx.beginPath()
				ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2)
				ctx.fillStyle = `rgba(255,255,255,${(p.baseOpacity * flicker).toFixed(3)})`
				ctx.fill()
			}

			rafRef.current = requestAnimationFrame(loop)
		}

		rafRef.current = requestAnimationFrame(loop)

		// Запуск проявки с задержкой
		const revealTimer = setTimeout(() => {
			setRevealing(true)
			setTimeout(() => setDone(true), REVEAL_DURATION_MS)
		}, REVEAL_DELAY_MS)

		return () => {
			cancelAnimationFrame(rafRef.current)
			clearTimeout(revealTimer)
		}
	}, [])

	// Остановить RAF после завершения или при ошибке
	useEffect(() => {
		if (done || broken) cancelAnimationFrame(rafRef.current)
	}, [done, broken])

	if (broken) {
		return (
			<div ref={wrapRef} className="gw-spoiler gw-spoiler--broken">
				<Logo size={64} className={className} />
			</div>
		)
	}

	return (
		<div ref={wrapRef} className="gw-spoiler">
			<img
				src={src}
				alt={alt ?? ''}
				className={`gw-spoiler__img${revealing ? ' gw-spoiler__img--revealed' : ''}${className ? ` ${className}` : ''}`}
				onError={() => setBroken(true)}
			/>
			{!done && (
				<canvas
					ref={canvasRef}
					className={`gw-spoiler__canvas${revealing ? ' gw-spoiler__canvas--out' : ''}`}
				/>
			)}
		</div>
	)
}
